import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageContext from './LanguageContext';
import { ChildrenProps } from 'interfaces/general.interface';
import { TLanguageResponseItem } from 'api/endpoints/general/general.response.interface';
import { useAuth0 } from '@auth0/auth0-react';
import { getLanguagesAPI } from 'api/endpoints/general/general.api';
import useContextGeneral from 'hooks/contexts/useContextGeneral';

export type TLangType = 'en-gb' | 'it' | 'fr' | 'es';
export const langs = {
  es: 'es',
  en: 'en-gb',
  fr: 'fr',
  it: 'it',
};

function LanguageProvider({ children }: ChildrenProps) {
  const { i18n } = useTranslation();
  const { ping } = useContextGeneral();
  const [language, setLanguage] = useState('es' as TLangType);
  const [languages, setLanguages] = useState([] as TLanguageResponseItem[]);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!ping) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const languagesFetch = await getLanguagesAPI({ token });
        if (languagesFetch.response.status === 200) {
          setLanguages(languagesFetch.data);
        }
      } catch (error) {
        console.log(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ping]);

  const setLanguageAction = useCallback(
    async (language: TLangType) => {
      document.documentElement.setAttribute('lang', language);
      setLanguage(language);
      i18n.changeLanguage(language);
    },
    [i18n]
  );

  const memorizedProviderLanguage = useMemo(
    () => ({
      language,
      languages,
      setLanguageAction,
    }),
    [language, languages, setLanguageAction]
  );

  return (
    <LanguageContext.Provider value={memorizedProviderLanguage}>
      {children}
    </LanguageContext.Provider>
  );
}

export default memo(LanguageProvider);
